import { useEffect, useMemo } from "react";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import UnifiedLabServiceSearchComponent from "./UnifiedLabServiceSearchComponent";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useClinicTamsFilterAPI } from "@/app/hooks/api_hooks/Inventory/useClinicTamsFilterAPI";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { toISTIso } from "../../SurgePricing/interface";

export interface ICheckPricesProps {
  cityId: string;
  clinics: Array<string>;
  // Optional surge context: appointment type + visit slot.
  serviceTypeId: string;
  visitDate: string;
  visitH: string;
  visitM: string;
}

const CLINIC_PRICE_ITEM: ISelectMenuItemData = { id: "", title: "Clinic Price" };

const CheckPrices = () => {
  const { cities } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const { mappedClinicId, mappedCityId } = useAppSelector((state) => state.root.UIStates);
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const { execute: logAPI } = useClickLogAPI();
  const cityOrgItem = useSelectMenuReducer(cities, "name", "id");
  const { execute: fetchClinicsTams, clinicList } = useClinicTamsFilterAPI();
  const clinicItems = useSelectMenuReducer(clinicList, "display_name", "id");
  const fetchCities = useOrgCitiesListAPI("INVENTORY");
  const { execute: fetchServiceTypes, serviceTypes } = useOrgServicesByFilterAPI();
  const rawServiceTypeItems = useSelectMenuReducer(serviceTypes, "name", "id");
  // Memoised so the dropdown doesn't refilter on every render.
  const serviceTypeItems = useMemo(() => [CLINIC_PRICE_ITEM, ...rawServiceTypeItems], [serviceTypes]); // eslint-disable-line react-hooks/exhaustive-deps
  const arrayConstants = useArrayConstants();
  const hourItems = arrayConstants.overBookingStartTimeItems as ISelectMenuItemData[];
  const minuteItems = arrayConstants.overBookingStartTimeMinuteItems as ISelectMenuItemData[];

  const formHook = useForm<ICheckPricesProps>({
    mode: "onChange",
    defaultValues: {
      cityId: mappedCityId ?? "",
      clinics: mappedClinicId ? [mappedClinicId] : [],
      serviceTypeId: "",
      visitDate: format(new Date(), "yyyy-MM-dd"),
      visitH: format(new Date(), "HH"),
      visitM: "00",
    },
  });

  const serviceTypeId = formHook.watch("serviceTypeId");
  const slotStart = toISTIso(formHook.watch("visitDate"), formHook.watch("visitH"), formHook.watch("visitM"));
  const timeSelect = (name: "visitH" | "visitM", items: ISelectMenuItemData[], placeholder: string) => (
    <div className="w-20 flex-shrink-0">
      <NonSearchableSelectMenu
        errorMessages={[]}
        onSelectItem={(item) => item && formHook.setValue(name, item.title)}
        fieldError={formHook.formState.errors[name]}
        register={formHook.register(name)}
        selectItems={items}
        placeholder={placeholder}
        showTooltips={false}
        showTypedErrors={false}
        showDropdownIcon={true}
        disabled={!serviceTypeId}
        defaultSelected={items.find((i) => i.title === formHook.watch(name))}
        listBoxClassName="w-full"
        className="text-gray-800"
      />
    </div>
  );

  const cityId = formHook.watch("cityId");
  const selectedClinicId = formHook.watch("clinics")?.[0];

  useEffect(() => {
    fetchCities();
    // Only types with active, bookable sub-services.
    fetchServiceTypes({ filterType: "staff_setup" });
  }, []);

  useEffect(() => {
    if (cityId) {
      fetchClinicsTams(cityId);
    }
  }, [cityId]);

  return (
    <div className="bg-white flex flex-col h-full rounded-md mx-auto w-full">
      <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 flex-shrink-0">
        <h2 className="text-base font-semibold text-gray-900">Check Service Price</h2>
      </div>
      <form className="flex-1 flex flex-col p-6 gap-6 min-h-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 flex-shrink-0 items-end">
          <div className="mt-1 lg:col-span-3">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Select City</label>
            <SearchableSelectMenu
              errorMessages={[{ type: "required", message: null }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("cityId", item.id);
                  const logBody: IClickLogBody = {
                    event_type: "CHECK_PRICE_CITY_FILTER_CLICK",
                    vms_user_id: userLogInfo,
                    scr_id: "CHECK_PRICE_MODAL",
                    filter_values: { city: { id: item.id, name: item.title ?? null } },
                    timestamp: Date.now(),
                  };
                  logAPI(logBody);
                }
              }}
              fieldError={formHook.formState.errors.cityId}
              register={formHook.register("cityId", { required: true })}
              selectItems={cityOrgItem}
              placeholder="Select City"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              defaultSelected={cityOrgItem?.filter((oc) => oc.id === formHook.watch("cityId"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="mt-1 lg:col-span-3">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Select Clinic</label>
            <SearchableSelectMenu
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("clinics", [item.id]);
                }
              }}
              fieldError={formHook.formState.errors.clinics}
              register={formHook.register("clinics")}
              selectItems={clinicItems}
              placeholder="Select Clinics"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={clinicItems?.find((oc) => oc.id === formHook.watch("clinics")?.[0])}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="lg:col-span-6">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Search Services & Lab Tests
            </label>
            <UnifiedLabServiceSearchComponent
              placeholder="Search Services & Lab Tests..."
              clinicId={selectedClinicId}
              portalId="check-prices-results-portal"
              serviceTypeId={serviceTypeId}
              slotStart={slotStart}
            />
          </div>
          <div className="mt-1 lg:col-span-3">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Appointment Type
            </label>
            <SearchableSelectMenu
              errorMessages={[]}
              onSelectItem={(item) => formHook.setValue("serviceTypeId", item?.id ?? "")}
              fieldError={formHook.formState.errors.serviceTypeId}
              register={formHook.register("serviceTypeId")}
              selectItems={serviceTypeItems}
              placeholder="Clinic Price"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={serviceTypeItems.find((t) => t.id === serviceTypeId)}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="mt-1 lg:col-span-5">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Visit Date & Time
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-40 flex-shrink-0">
                <PWLCalendarComponent
                  showFutureYears
                  disabled={!serviceTypeId}
                  errorMessages={[]}
                  register={formHook.register("visitDate")}
                  fieldError={formHook.formState.errors.visitDate}
                  onSelectDate={(date) => formHook.setValue("visitDate", format(date, "yyyy-MM-dd"))}
                  defaultSelectedDate={formHook.watch("visitDate")}
                  className="w-full"
                />
              </div>
              {timeSelect("visitH", hourItems, "HH")}
              {timeSelect("visitM", minuteItems, "MM")}
            </div>
          </div>
        </div>
        <div className="border-t border-gray-100 mt-4 pt-4 flex-1 min-h-0 flex flex-col">
          <div id="check-prices-results-portal" className="flex-1 min-h-0" />
        </div>
      </form>
    </div>
  );
};

export default CheckPrices;
